import React, { memo, CSSProperties } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Tooltip } from 'antd';
import { ProtocolFlowNodeData, PROTOCOL_NODE_CONFIG } from '../../../../../types/lab/ProtocolTypes';
import { PROTOCOL_STEP_ICONS } from './protocolIcons';

// ── Stat badge helper ─────────────────────────────────────────────────────────
const StatBadge: React.FC<{ label: string; count: number; color?: string }> = ({
  label,
  count,
  color = 'var(--mg-text-secondary)',
}) => {
  if (count === 0) return null;
  return (
    <span
      style={{
        fontSize: 9,
        fontWeight: 600,
        color,
        background: 'var(--mg-bg-subtle)',
        border: '1px solid var(--mg-border)',
        borderRadius: 4,
        padding: '1px 4px',
        marginRight: 3,
      }}
    >
      {count} {label}
    </span>
  );
};

// ── BaseProtocolNode ──────────────────────────────────────────────────────────
interface BaseProtocolNodeProps {
  data: ProtocolFlowNodeData;
  selected?: boolean;
  /** If true, no target (top) handle — e.g. start node. */
  noTarget?: boolean;
  /** If true, no source (bottom) handle — e.g. end node. */
  noSource?: boolean;
  /** Custom source handles for branching nodes (conditional). */
  sourceHandles?: { id: string; label: string; style?: CSSProperties }[];
}

const BaseProtocolNode: React.FC<BaseProtocolNodeProps> = ({
  data,
  selected,
  noTarget,
  noSource,
  sourceHandles,
}) => {
  const cfg = PROTOCOL_NODE_CONFIG[data.step_type];
  const methodCount = data.method?.length ?? 0;
  const materialCount = data.materials?.length ?? 0;
  const fieldCount = data.configuration?.required_fields?.length ?? 0;
  const equipmentCount = data.configuration?.equipment_requirements?.length ?? 0;
  const hasConditions = !!(
    data.completion_conditions?.conditions?.length ||
    data.completion_actions?.length ||
    data.failure_actions?.length ||
    data.configuration?.automation_actions?.length
  );

  const borderColor = selected ? 'var(--mg-primary)' : `${cfg.color}55`;
  const boxShadow = selected
    ? `0 0 0 3px var(--mg-primary-bg), 0 2px 8px rgba(0,0,0,0.15)`
    : '0 2px 6px rgba(0,0,0,0.08)';

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      {/* Step number badge */}
      {data.step_number != null && (
        <div
          style={{
            position: 'absolute',
            top: -9,
            right: -9,
            width: 20,
            height: 20,
            borderRadius: '50%',
            background: 'var(--mg-bg-elevated)',
            border: `2px solid ${cfg.color}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 9,
            fontWeight: 700,
            color: cfg.color,
            zIndex: 10,
          }}
        >
          {data.step_number}
        </div>
      )}

      {/* Main node card */}
      <div
        style={{
          minWidth: 190,
          maxWidth: 230,
          borderRadius: 10,
          border: `2px solid ${borderColor}`,
          background: 'var(--mg-bg-elevated)',
          boxShadow,
          overflow: 'hidden',
          transition: 'border-color 0.15s, box-shadow 0.15s',
        }}
      >
        {/* Coloured header */}
        <div
          style={{
            background: cfg.color,
            padding: '6px 10px',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            color: '#fff',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
          }}
        >
          {PROTOCOL_STEP_ICONS[data.step_type]}
          {cfg.label}
        </div>

        {/* Body */}
        <div style={{ padding: '8px 10px' }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: 13,
              color: 'var(--mg-text)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {data.label || 'Untitled'}
          </div>

          {data.description && (
            <div
              style={{
                fontSize: 11,
                color: 'var(--mg-text-secondary)',
                marginTop: 2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {data.description}
            </div>
          )}

          {/* Stat badges row */}
          {(methodCount > 0 || materialCount > 0 || fieldCount > 0 || equipmentCount > 0 || hasConditions) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginTop: 5 }}>
              <StatBadge count={methodCount} label="methods" />
              <StatBadge count={materialCount} label="materials" />
              <StatBadge count={fieldCount} label="fields" />
              <StatBadge count={equipmentCount} label="equip." />
              {hasConditions && (
                <StatBadge count={1} label="logic" color={cfg.color} />
              )}
            </div>
          )}

          {/* Duration badge for lab steps */}
          {data.duration_estimate != null && data.duration_estimate > 0 && (
            <div
              style={{
                marginTop: 5,
                fontSize: 10,
                color: 'var(--mg-text-secondary)',
              }}
            >
              ⏱ {data.duration_estimate} min
            </div>
          )}
        </div>

        {/* Target handle (top) */}
        {!noTarget && (
          <Handle
            type="target"
            position={Position.Top}
            style={{
              width: 10,
              height: 10,
              background: 'var(--mg-border-strong)',
              border: '2px solid var(--mg-bg-elevated)',
            }}
          />
        )}

        {/* Source handle(s) (bottom) */}
        {!noSource && (
          sourceHandles ? (
            sourceHandles.map((h) => (
              <Tooltip key={h.id} title={h.label} placement="bottom">
                <Handle
                  type="source"
                  position={Position.Bottom}
                  id={h.id}
                  style={{
                    width: 10,
                    height: 10,
                    background: cfg.color,
                    border: '2px solid var(--mg-bg-elevated)',
                    ...h.style,
                  }}
                />
              </Tooltip>
            ))
          ) : (
            <Handle
              type="source"
              position={Position.Bottom}
              style={{
                width: 10,
                height: 10,
                background: cfg.color,
                border: '2px solid var(--mg-bg-elevated)',
              }}
            />
          )
        )}
      </div>
    </div>
  );
};

// ── Individual node components ────────────────────────────────────────────────

export const StartProtocolNode = memo(({ data, selected }: NodeProps) => (
  <BaseProtocolNode
    data={data as ProtocolFlowNodeData}
    selected={selected}
    noTarget
  />
));

export const ManualStepNode = memo(({ data, selected }: NodeProps) => (
  <BaseProtocolNode data={data as ProtocolFlowNodeData} selected={selected} />
));

export const AutomatedStepNode = memo(({ data, selected }: NodeProps) => (
  <BaseProtocolNode data={data as ProtocolFlowNodeData} selected={selected} />
));

export const QCNode = memo(({ data, selected }: NodeProps) => (
  <BaseProtocolNode data={data as ProtocolFlowNodeData} selected={selected} />
));

export const ResultsStepNode = memo(({ data, selected }: NodeProps) => (
  <BaseProtocolNode data={data as ProtocolFlowNodeData} selected={selected} />
));

export const AliquotNode = memo(({ data, selected }: NodeProps) => (
  <BaseProtocolNode data={data as ProtocolFlowNodeData} selected={selected} />
));

/** Conditional node — two source handles: Yes (left) and No (right). */
export const ConditionalProtocolNode = memo(({ data, selected }: NodeProps) => (
  <BaseProtocolNode
    data={data as ProtocolFlowNodeData}
    selected={selected}
    sourceHandles={[
      { id: 'condition_yes', label: 'Yes', style: { left: '30%' } },
      { id: 'condition_no',  label: 'No',  style: { left: '70%' } },
    ]}
  />
));

export const EndProtocolNode = memo(({ data, selected }: NodeProps) => (
  <BaseProtocolNode
    data={data as ProtocolFlowNodeData}
    selected={selected}
    noSource
  />
));

// ── nodeTypes map for <ReactFlow nodeTypes={...}> ─────────────────────────────
export const protocolNodeTypes = {
  start:       StartProtocolNode,
  manual:      ManualStepNode,
  automated:   AutomatedStepNode,
  qc:          QCNode,
  results:     ResultsStepNode,
  aliquot:     AliquotNode,
  conditional: ConditionalProtocolNode,
  end:         EndProtocolNode,
} as const;
